import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Image, Clipboard, Pill, Upload, Clock, Filter } from 'lucide-react';
import { getPatientHistory } from '../services/patientService';
import { toast } from 'react-toastify';

interface TimelineEvent {
  id: number;
  type: string;
  title: string;
  subtitle: string;
  date: string;
  status: string | null;
}

interface PatientHistoryData {
  patient_id: string;
  timeline: TimelineEvent[];
  counts: {
    clinical_notes: number;
    imaging_reports: number;
    histology_reports: number;
    treatment_histories: number;
    mdt_cases: number;
    mdt_reports: number;
    medical_documents: number;
    total: number;
  };
}

interface PatientHistoryProps {
  patientId: string;
}

const eventTypeConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  clinical_note: { icon: FileText, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  imaging: { icon: Image, color: 'text-purple-600', bgColor: 'bg-purple-100' },
  histology: { icon: FileText, color: 'text-pink-600', bgColor: 'bg-pink-100' },
  treatment: { icon: Pill, color: 'text-green-600', bgColor: 'bg-green-100' },
  mdt_case: { icon: Clipboard, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  mdt_report: { icon: FileText, color: 'text-red-600', bgColor: 'bg-red-100' },
  document: { icon: Upload, color: 'text-teal-600', bgColor: 'bg-teal-100' },
};

const eventTypeLabels: Record<string, string> = {
  clinical_note: 'Clinical Note',
  imaging: 'Imaging Report',
  histology: 'Histology Report',
  treatment: 'Treatment',
  mdt_case: 'MDT Case',
  mdt_report: 'MDT Report',
  document: 'Document',
};

const getStatusColor = (status: string | null) => {
  if (!status) return '';
  switch (status.toLowerCase()) {
    case 'approved':
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'draft':
      return 'bg-gray-100 text-gray-800';
    case 'open':
    case 'under_review':
      return 'bg-yellow-100 text-yellow-800';
    case 'mdt_ready':
      return 'bg-blue-100 text-blue-800';
    case 'pending':
      return 'bg-orange-100 text-orange-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const PatientHistory: React.FC<PatientHistoryProps> = ({ patientId }) => {
  const [historyData, setHistoryData] = useState<PatientHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPatientHistory(patientId);
      setHistoryData(data);
    } catch (error) {
      console.error('Error fetching patient history:', error);
      toast.error('Failed to load patient history');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredTimeline = historyData?.timeline.filter(event => {
    if (activeFilter === 'all') return true;
    return event.type === activeFilter;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!historyData || historyData.timeline.length === 0) {
    return (
      <div className="card text-center py-12">
        <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No History Available</h3>
        <p className="text-gray-600">No clinical events have been recorded for this patient yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {Object.entries(historyData.counts).filter(([key]) => key !== 'total').map(([key, count]) => (
          <div
            key={key}
            className={`p-3 rounded-lg border cursor-pointer transition-colors ${
              activeFilter === key
                ? 'bg-primary-50 border-primary-300'
                : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => setActiveFilter(activeFilter === key ? 'all' : key)}
          >
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{count}</div>
              <div className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter Info */}
      {activeFilter !== 'all' && (
        <div className="flex items-center justify-between bg-primary-50 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-primary-600" />
            <span className="text-sm text-primary-700">
              Showing {filteredTimeline.length} {eventTypeLabels[activeFilter] || activeFilter} events
            </span>
          </div>
          <button
            onClick={() => setActiveFilter('all')}
            className="text-sm text-primary-600 hover:text-primary-800"
          >
            Clear Filter
          </button>
        </div>
      )}

      {/* Timeline */}
      <div className="relative">
        {/* Vertical Line */}
        <div className="absolute left-4 sm:left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        {/* Timeline Events */}
        <div className="space-y-4">
          {filteredTimeline.map((event, index) => {
            const config = eventTypeConfig[event.type] || eventTypeConfig.document;
            const Icon = config.icon;

            return (
              <div key={`${event.type}-${event.id}-${index}`} className="relative flex items-start space-x-4 pl-4 sm:pl-8">
                {/* Timeline Dot */}
                <div className={`absolute left-2 sm:left-6 w-5 h-5 rounded-full ${config.bgColor} flex items-center justify-center z-10`}>
                  <Icon className={`h-3 w-3 ${config.color}`} />
                </div>

                {/* Event Card */}
                <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow ml-4 sm:ml-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${config.bgColor} ${config.color}`}>
                          {eventTypeLabels[event.type] || event.type}
                        </span>
                        {event.status && (
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(event.status)}`}>
                            {event.status.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      <h4 className="font-medium text-gray-900">{event.title}</h4>
                      {event.subtitle && (
                        <p className="text-sm text-gray-500 mt-0.5">{event.subtitle}</p>
                      )}
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <div>{formatDate(event.date)}</div>
                      <div className="text-xs">{formatTime(event.date)}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PatientHistory;
